import { useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'

export type PickerUser = {
  id: string
  /** Absente quand le lecteur ne voit pas ce compte (adresse d'un admin, pour un formateur). */
  email?: string | null
  firstName: string
  lastName: string
  groups?: { id: string, name: string }[] | null
}

export type PickerGroup = {
  id: string
  name: string
  memberCount: number
}

type IProps = {
  users: PickerUser[]
  value: Set<string>
  onChange: (next: Set<string>) => void
  /**
   * Groupes proposés en raccourci d'inscription. Omis = pas de raccourci (le
   * picker sert aussi à composer les groupes eux-mêmes, où il n'aurait pas de sens).
   */
  groups?: PickerGroup[]
  /** Clé i18n du titre. Par défaut « Participants ». */
  labelKey?: string
  /**
   * Clé i18n du nom du champ de recherche. Par défaut « Rechercher un participant ».
   * Deux pickers sur un même écran doivent dire ce que chacun cherche (RGAA 11.1).
   */
  searchLabelKey?: string
}

/**
 * Sélecteur de participants : recherche + sélection en masse (cases à cocher,
 * tout sélectionner / désélectionner sur le résultat filtré). Contrôlé —
 * l'ensemble sélectionné vit dans le parent.
 *
 * Avec `groups`, un rang de raccourcis permet d'inscrire un groupe entier en une
 * action (#135). Le raccourci ajoute les membres à la sélection au lieu de la
 * remplacer : on compose ainsi « le client A plus deux apprenants du client B ».
 */
function ParticipantPicker({ users, value, onChange, groups, labelKey, searchLabelKey }: IProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return users
    return users.filter(u =>
      `${u.firstName} ${u.lastName} ${u.email ?? ''}`.toLowerCase().includes(q),
    )
  }, [users, search])

  // Membres par groupe, déduits des utilisateurs déjà chargés : évite une requête
  // par groupe, et le picker travaille de toute façon sur cette liste.
  const membersByGroup = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const user of users) {
      for (const group of user.groups ?? []) {
        const current = map.get(group.id)
        if (current) current.push(user.id)
        else map.set(group.id, [user.id])
      }
    }
    return map
  }, [users])

  const toggle = (id: string) => {
    const next = new Set(value)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }

  const selectAllFiltered = () => {
    const next = new Set(value)
    filtered.forEach(u => next.add(u.id))
    onChange(next)
  }

  const clearFiltered = () => {
    const next = new Set(value)
    filtered.forEach(u => next.delete(u.id))
    onChange(next)
  }

  const addGroup = (groupId: string) => {
    const next = new Set(value)
    ;(membersByGroup.get(groupId) ?? []).forEach(id => next.add(id))
    onChange(next)
  }

  const titleId = useId()
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span id={titleId} className="text-sm font-medium">
          {t(labelKey ?? 'common.participants')}
          {' ('}
          {value.size}
          )
        </span>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={selectAllFiltered} disabled={filtered.length === 0}>
            {t('sessions.participants.selectAll')}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={clearFiltered} disabled={value.size === 0}>
            {t('sessions.participants.clear')}
          </Button>
        </div>
      </div>

      {groups && groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border p-2">
          <span className="text-xs text-muted-foreground">
            {t('sessions.participants.addGroup')}
          </span>
          {groups.map(group => (
            <Button
              key={group.id}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addGroup(group.id)}
              disabled={(membersByGroup.get(group.id)?.length ?? 0) === 0}
            >
              {group.name}
              {' '}
              <span className="ml-1 text-muted-foreground">
                (
                {group.memberCount}
                )
              </span>
            </Button>
          ))}
        </div>
      )}

      <Input
        aria-label={t(searchLabelKey ?? 'sessions.participants.search')}
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder={t(searchLabelKey ?? 'sessions.participants.search')}
      />

      {/* Les cases appartiennent à la liste des participants : le groupe le dit (RGAA 11.5). */}
      <div role="group" aria-labelledby={titleId} className="max-h-64 overflow-y-auto rounded-md border border-border p-2">
        {filtered.length === 0
          ? <p className="p-2 text-sm text-muted-foreground">{t('sessions.noUsers')}</p>
          : filtered.map(u => (
              <label
                key={u.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted"
              >
                <input type="checkbox" checked={value.has(u.id)} onChange={() => toggle(u.id)} />
                <span className="font-medium">
                  {u.firstName}
                  {' '}
                  {u.lastName}
                </span>
                <span className="text-muted-foreground">{u.email}</span>
              </label>
            ))}
      </div>
    </div>
  )
}

export default ParticipantPicker
